import { useRef, useEffect, useCallback } from "react"
import { GlobalAIMessage } from "./GlobalAIMessage"
import { GlobalAIThinking } from "./GlobalAIThinking"
import type { GlobalAIMessage as GlobalAIMessageType, ToolCall } from "../types"

interface GlobalAIChatProps {
  messages: GlobalAIMessageType[]
  isStreaming: boolean
  currentText: string
  currentToolCalls: ToolCall[]
}

export function GlobalAIChat({
  messages,
  isStreaming,
  currentText,
  currentToolCalls,
}: GlobalAIChatProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const isUserScrolledUp = useRef(false)

  // Track if user manually scrolled up
  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current
    // Consider "at bottom" if within 100px of bottom
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 100
    isUserScrolledUp.current = !isAtBottom
  }, [])

  // Auto-scroll to bottom when new content arrives (only if user hasn't scrolled up)
  useEffect(() => {
    if (scrollRef.current && !isUserScrolledUp.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages, currentText, currentToolCalls])

  // Reset scroll tracking when streaming ends
  useEffect(() => {
    if (!isStreaming) {
      isUserScrolledUp.current = false
    }
  }, [isStreaming])

  return (
    <div ref={scrollRef} onScroll={handleScroll} className="h-full overflow-y-auto p-4 space-y-4">
      {/* Existing messages */}
      {messages.map((message, index) => (
        <GlobalAIMessage key={index} message={message} />
      ))}

      {/* Streaming content */}
      {isStreaming && (
        <>
          {/* Show tool calls as they happen */}
          {currentToolCalls.length > 0 && (
            <div className="space-y-1">
              {currentToolCalls.map((tool, index) => (
                <div
                  key={index}
                  className="text-xs text-muted-foreground flex items-center gap-2 pl-2"
                >
                  <span className="inline-block w-2 h-2 bg-primary rounded-full animate-pulse" />
                  <span>{tool.output_summary || `Using ${tool.name}...`}</span>
                </div>
              ))}
            </div>
          )}

          {/* Show thinking indicator or streaming text */}
          {currentText ? (
            <GlobalAIMessage
              message={{
                role: "assistant",
                content: currentText,
                timestamp: Math.floor(Date.now() / 1000),
                tool_calls: currentToolCalls,
              }}
              isStreaming
            />
          ) : (
            <GlobalAIThinking />
          )}
        </>
      )}

    </div>
  )
}
